import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { ReviewContentReviewData } from '@shared/api/types/review';
import { useAuth } from '@shared/common/providers/AuthProvider';

import { useLikeReviewMutation } from '@query/app';

import { getErrorMessage } from '@utilities/errors/getErrorMessage';

const useReviewFeedbackLike = ({ review }: { review: ReviewContentReviewData }) => {
  const initialData = review.userFunnyVote || false;
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const queryKey = ['reviewLike', user?.id, review.id, review.userFunnyVote];
  const { data: oldValue } = useQuery<boolean>({
    queryKey,
    staleTime: Infinity,
    queryFn: () => initialData,
    initialData,
  });
  const [nextValue, setNextValue] = useState<boolean | null>(null);
  const value = nextValue === null ? oldValue : nextValue;

  const { mutateAsync, isPending } = useLikeReviewMutation({
    onMutate: ({ like }) => {
      const previousData = queryClient.getQueryData<boolean>(queryKey);

      queryClient.setQueryData(queryKey, like);

      return previousData;
    },
    onError: (error, variables, like) => {
      queryClient.setQueryData(queryKey, like);
    },
  });

  // // Reset state in case initialData change to have fresh data
  // // e.g. due to react query refetch
  // useLayoutEffect(() => {
  //   setNextValue(null);
  //   queryClient.setQueryData(queryKey, initialData);
  // }, [queryClient, initialData, queryKey]);

  useEffect(() => {
    if (isPending || oldValue === value) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      try {
        await mutateAsync({
          reviewId: review.id,
          reviewType: review.type,
          like: value,
        });
      } catch (error) {
        toast.error(getErrorMessage(error));
      } finally {
        setNextValue(null);
      }
    }, 500);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isPending, value, mutateAsync, oldValue, review.id, review.type]);

  return [value, setNextValue] as const;
};

export default useReviewFeedbackLike;
